import {
    AppEnvironment, getEnvironment,
} from 'appconfig'
import {
    InvalidConfigKeyErrorEvent,
    InvalidSiteConfigErrorEvent,
    SiteConfigFetchErrorEvent,
} from 'embed/types/errorEvents'
import {
    NinchatEmbedEnvironment,
    NinchatEnvironmentConfig,
    NinchatOptions,
    NinchatSiteConfig,
} from 'embed/types'
import {ConfigKeySchema} from 'embed/schemas'
import {EmbedErrorType} from 'embed/types/errorTypes'
import {EmbedEvent} from 'embed/types/events'
import {ZodError} from 'zod'

import {merge} from 'lodash'

export type BuildConfigProps = Pick<NinchatOptions, 'config' | 'configKey' | 'configUrls' | 'environment' | 'postConfigUrls'> & {
    appEnvironment?: AppEnvironment
}

const deepMergeKeys = ['floatingOptions', 'containerOptions', 'theme']

// customFonts are merged together across all environments and configurations, duplicates need to be removed
const dedupeFonts = <T>(fonts: T[]): T[] => {
    const seen = new Set<string>()
    return fonts.filter((font) => {
        const key = JSON.stringify(font)
        if (seen.has(key)) {
            return false
        }
        seen.add(key)
        return true
    })
}

const mergeCore = (nestedKey: string, baseConfig: Record<string, any>, config: Record<string, any>) => {
    let configuration = {...baseConfig}


    if (deepMergeKeys.includes(nestedKey)) {
        if (nestedKey === 'theme') {
            const fonts = dedupeFonts([...baseConfig[nestedKey]?.customFonts || [], ...config[nestedKey]?.customFonts || []])

            configuration = {
                ...configuration,
                [nestedKey]: {
                    ...merge({}, baseConfig[nestedKey] || {}, config[nestedKey] || {}),
                    customFonts: fonts,
                },
            }
        } else {
            configuration = {
                ...configuration,
                [nestedKey]: merge({}, baseConfig[nestedKey] || {}, config[nestedKey] || {}),
            }
        }
    } else {
        configuration = {
            ...configuration,
            [nestedKey]: typeof config[nestedKey] === 'object' && !Array.isArray(config[nestedKey])
                ? {
                    ...baseConfig[nestedKey],
                    ...config[nestedKey],
                }
                : config[nestedKey],
        }
    }

    return configuration
}


const mergeEnvs = (environmentConfigs: NinchatEnvironmentConfig[]): NinchatEnvironmentConfig => {
    let configuration = Object.assign({}, ...environmentConfigs)

    environmentConfigs.forEach((envConfig: NinchatEnvironmentConfig & Record<string, any>) => {
        if (typeof envConfig === 'object') {
            const nestedKeys = Object.keys(envConfig)
            nestedKeys.forEach((nestedKey) => {
                configuration = mergeCore(nestedKey, configuration, envConfig)
            })
        }
    })

    return configuration as NinchatEnvironmentConfig
}


const mergeConfigs = (configs: NinchatSiteConfig[]): NinchatSiteConfig => {
    const configuration: NinchatSiteConfig & Record<string, any> = Object.assign({}, ...configs)
    // Merge 2nd level of config object.

    configs.forEach((config: NinchatSiteConfig & Record<string, any>) => {
        for (const key in config) {
            if (typeof configuration[key] === 'object' && !Array.isArray(configuration[key])) {
                const nestedKeys = Object.keys(config[key])
                nestedKeys.forEach((nestedKey) => {
                    configuration[key] = mergeCore(nestedKey, configuration[key], config[key])
                })
            } else {
                configuration[key] = config[key]
            }
        }
    })

    return configuration
}

const buildEnvironment = (configs: NinchatSiteConfig[], environment: NinchatEmbedEnvironment): NinchatEnvironmentConfig => {
    const mergedConfig = mergeConfigs(configs)
    // todo ( currently disabled to support react native sdk and old site config that it uses)
    // SiteConfigSchema.parse(mergedConfig)

    // Add 'default' to the beginning, and remove from elsewhere, if exists
    const sortedEnvironment: NinchatEmbedEnvironment = [...['default'], ...(environment || []).filter((item) => item !== 'default')]


    // Create final config by shallow merging environments set in environment attr
    const environments = sortedEnvironment.map((key) => mergedConfig[key as keyof NinchatSiteConfig]) as NinchatEnvironmentConfig[]

    return mergeEnvs(environments)
}

const fetchConfigs = async (props: BuildConfigProps, targetOrigin: string) => {
    const {
        apiHost,
        staticSiteURL,
    } = props.appEnvironment || getEnvironment()

    const {
        configKey,
        configUrls,
        postConfigUrls,
    } = props


    try {
        ConfigKeySchema.parse(configKey)
    } catch (error) {
        const errorEvent: InvalidConfigKeyErrorEvent = {
            error_reason: 'Accepted configKey format is \'site/REALM_ID/SITE_ID.json\'',
            error_type: EmbedErrorType.InvalidConfigKeyError,
            event: EmbedEvent.Error,
            targetOrigin,
        }

        return Promise.reject(errorEvent)
    }

    const urls = [
        `${staticSiteURL}/assets/ninchat-defaults.json`,
        `https://${apiHost}/config/${configKey}`,
    ]

    const onResponse = (response: Response) => {
        if (response.ok) {
            return response.json()
        }

        const {
            status,
            statusText,
            url,
        } = response

        const errorEvent: SiteConfigFetchErrorEvent = {
            error_type: EmbedErrorType.SiteConfigFetchError,
            event: EmbedEvent.Error,
            status,
            statusText,
            targetOrigin,
            url,
        }

        return Promise.reject(errorEvent)
    }

    // To avoid preflight OPTIONS requests, ensure the request uses simple CORS rules:
    // - Use GET or POST methods.
    // - Use only simple headers: 'Content-Type', 'Accept', etc.
    // - Use 'application/x-www-form-urlencoded', 'multipart/form-data', or 'text/plain' as Content-Type.
    // const config = {headers: {'Content-Type': 'text/plain'}}

    const config: any = {
        cache: 'no-store',
        headers: {'Content-Type': 'text/plain'},
    }


    const promises = [
        ...urls.map((url) => fetch(url, config).then(onResponse)),
        ...(configUrls || []).map((url) => fetch(url, config).then(onResponse)),
        ...(postConfigUrls || []).map((url) => fetch(url, {method: 'POST'}).then(onResponse)),
    ]

    const configs = await Promise.all(promises)

    return configs
}

export const buildConfig = async (props: BuildConfigProps, targetOrigin: string) => {
    const {
        config,
        environment,
    } = props

    const configs = await fetchConfigs(props, targetOrigin)

    if (config) {
        configs.push(config)
    }

    try {
        return buildEnvironment(configs, environment)
    } catch (error) {
        const {issues} = error as ZodError

        const errorEvent: InvalidSiteConfigErrorEvent = {
            error_type: EmbedErrorType.InvalidSiteConfigError,
            event: EmbedEvent.Error,
            issues,
            targetOrigin,
        }

        return Promise.reject(errorEvent)
    }
}
